import React from "react";
import { apiService } from "../../../../../api/api";
import { ROUTES } from "../../../../../config/routes";
import {
  OacActivityStatus,
  OacChannelLastUpdateRow,
  getOacActivityStatus,
} from "../../../../OACAudit/oacActivityStatus";
import { WidgetComponentProps } from "../../types";
import { useWidgetData } from "../../useWidgetData";
import {
  CardEmpty,
  CardError,
  CardLoading,
  Freshness,
  Kpi,
  KpiGrid,
  countTone,
} from "../../ui";

interface OacData {
  rows: OacChannelLastUpdateRow[];
  updatedAt: string | null;
}

const OacAuditCard: React.FC<WidgetComponentProps> = () => {
  const { data, loading, error, reload } = useWidgetData<OacData>("oac-audit", async () => {
    const res = await apiService.oacAuditChannelIds.getChannelLastUpdates();
    return { rows: Array.isArray(res?.data) ? res.data : [], updatedAt: res?.updatedAt ?? null };
  });

  if (loading && !data) return <CardLoading />;
  if (error) return <CardError message={error} onRetry={reload} />;
  if (!data) return null;

  if (data.rows.length === 0) {
    return (
      <>
        <CardEmpty icon="pi pi-inbox" title="No channels in the OAC audit list" />
        <Freshness at={data.updatedAt} />
      </>
    );
  }

  // Same rules as the OAC Audit page (views/OACAudit/oacActivityStatus.ts).
  const counts: Record<OacActivityStatus, number> = { compliant: 0, scheduled: 0, noActivity: 0 };
  data.rows.forEach((row) => {
    counts[getOacActivityStatus(row)] += 1;
  });

  return (
    <>
      <KpiGrid columns={3}>
        <Kpi
          label="No activity this month"
          value={counts.noActivity}
          tone={countTone(counts.noActivity, "bad")}
          hint="No upload, publish or schedule"
          to={ROUTES.OAC_AUDIT}
          emphasis
        />
        <Kpi
          label="Scheduled only"
          value={counts.scheduled}
          tone={countTone(counts.scheduled)}
          hint="Nothing live yet this month"
          to={ROUTES.OAC_AUDIT}
        />
        <Kpi
          label="Compliant"
          value={counts.compliant}
          tone={counts.compliant > 0 ? "good" : "neutral"}
          hint={`of ${data.rows.length} channels`}
          to={ROUTES.OAC_AUDIT}
        />
      </KpiGrid>

      <div className="home-card__meta">
        <Freshness at={data.updatedAt} />
      </div>
    </>
  );
};

export default OacAuditCard;
